import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DonateModal from './DonateModal';

const LOGO_URL = '/images/gtra-logo.svg';

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [donateOpen, setDonateOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change via click
  const closeMobile = () => setMobileOpen(false);

  const toggleLang = () => {
    i18n.changeLanguage(i18n.language === 'zh' ? 'en' : 'zh');
  };

  const navLinks = [
    { key: 'nav.partners', to: '/partners' },
    { key: 'nav.events', to: '/events' },
    { key: 'nav.guests', to: '/guests' },
    { key: 'nav.contact', to: '/about#contact' },
  ];

  const navBg = scrolled
    ? 'bg-[#0A2540]/95 backdrop-blur-md shadow-lg'
    : 'bg-transparent';

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${navBg}`}>
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-20 md:h-24 flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 cursor-pointer whitespace-nowrap shrink-0" onClick={closeMobile}>
            <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center">
              <img src={LOGO_URL} alt="GTRA Logo" className="w-full h-full object-contain" />
            </div>
            <div className="flex flex-col leading-tight">
              <span className="text-white font-bold text-sm md:text-base tracking-wide">GTRA</span>
              <span className="text-white/70 text-[10px] hidden md:block tracking-widest uppercase">
                {i18n.language === 'zh' ? '全球科创圆桌联盟' : 'Global Tech Roundtable'}
              </span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-0.5">
            {navLinks.map(({ key, to }) => (
              <Link
                key={key}
                to={to}
                className="px-3 py-2 text-sm font-medium rounded-md transition-all duration-200 cursor-pointer whitespace-nowrap text-white/80 hover:text-white hover:bg-white/10"
              >
                {t(key)}
              </Link>
            ))}
          </div>

          {/* Right Side */}
          <div className="flex items-center gap-2">
            {/* Donate Button */}
            <button
              onClick={() => setDonateOpen(true)}
              className="hidden md:inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#2C7DA0]/80 hover:bg-[#2C7DA0] text-white text-xs font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-heart-line" />
              </div>
              {t('nav.donate')}
            </button>

            {/* Lang Toggle */}
            <button
              onClick={toggleLang}
              className="hidden md:flex items-center gap-1 px-3 py-1.5 rounded-full border border-white/30 text-white/80 hover:text-white hover:border-white/60 text-xs font-medium transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-global-line text-sm" />
              </div>
              <span>{i18n.language === 'zh' ? t('common.lang.en') : t('common.lang.zh')}</span>
            </button>

            {/* Hamburger */}
            <button
              className="lg:hidden w-8 h-8 flex items-center justify-center text-white cursor-pointer"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle menu"
            >
              <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`} />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <div
        className={`fixed inset-0 z-40 transition-all duration-300 ${mobileOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
      >
        <div className="absolute inset-0 bg-black/50" onClick={closeMobile} />
        <div
          className={`absolute top-0 right-0 w-72 h-full bg-[#0A2540] flex flex-col pt-20 px-6 transition-transform duration-300 overflow-y-auto ${mobileOpen ? 'translate-x-0' : 'translate-x-full'}`}
        >
          {navLinks.map(({ key, to }) => (
            <Link
              key={key}
              to={to}
              className="py-4 border-b border-white/10 text-white/80 hover:text-white text-base font-medium transition-colors cursor-pointer"
              onClick={closeMobile}
            >
              {t(key)}
            </Link>
          ))}

          {/* Donate Mobile */}
          <button
            onClick={() => { setDonateOpen(true); closeMobile(); }}
            className="mt-4 flex items-center gap-2 py-3 px-4 bg-[#2C7DA0] rounded-xl text-white text-sm font-semibold cursor-pointer whitespace-nowrap"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-heart-line" />
            </div>
            {t('nav.donate')}
          </button>

          <div className="mt-6">
            <button
              onClick={() => { toggleLang(); closeMobile(); }}
              className="flex items-center gap-2 text-white/80 hover:text-white text-sm cursor-pointer whitespace-nowrap"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-global-line" />
              </div>
              <span>{i18n.language === 'zh' ? 'Switch to English' : '切换为中文'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Donate Modal */}
      {donateOpen && <DonateModal onClose={() => setDonateOpen(false)} />}
    </>
  );
}
